import { AddAssetCard } from '@/components/Cards/AddAssetCard';
import { ImageAssetCard } from '@/components/Cards/ImageAssetCard';
import { useDeleteImage } from '@/hooks/useDeleteImage';
import { useGetBackgroundsList } from '@/hooks/useGetBackgroundsList';
import { useUploadBackgroundImage } from '@/hooks/useUploadBackgroundImage';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import theme from '@/theme';
import { annoynmousUserToEditorPagePostSsoReturnTo } from '@/utils/utils';
import { IBackground, ImageType } from '@d-id/types';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { useCallback, useState } from 'react';
import { isSelected } from './BackgroundPanel';

const StyledImageAssetCard = styled(ImageAssetCard)<{ selected: boolean }>`
    aspect-ratio: 1;

    :hover {
        outline: 2px solid ${({ theme }) => theme.colors.primary.main};
        outline-offset: -3px;
    }
`;

const UploadButton = styled(AddAssetCard)`
    font-weight: 600;
    aspect-ratio: 1;
`;

const UploadedBackgrounds = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
`;

export const MyBackgrounds = observer(() => {
    const { store } = useAdvancedEditorContext();
    const { isAnonymous, loginWithRedirect } = useAuth();
    const mixpanel = useAnalytics();
    const { data: backgrounds } = useGetBackgroundsList();
    const { mutateAsync: deleteImage } = useDeleteImage(ImageType.Backgrounds);
    const [indexDeleting, setIndexDeleting] = useState(-1);

    const onInputChange = useUploadBackgroundImage();

    const onUploadClick = useCallback((_: any, toggle: (state: boolean) => void) => {
        if (isAnonymous) {
            mixpanel?.track('go-to-signup', { event: 'click', from: 'upload-box', isAnonymous });
            loginWithRedirect({ appState: { returnTo: annoynmousUserToEditorPagePostSsoReturnTo } });
        } else {
            const fileInput = document.createElement('input');
            fileInput.type = 'file';
            fileInput.accept = 'image/*';
            fileInput.oninput = (e: any) => {
                toggle(true);
                onInputChange(e, () => toggle(false));
            };
            fileInput.click();
            fileInput.remove();
        }
    }, []);

    const onBackgroundClick = (background: IBackground) => {
        store.activePage.set({
            background: background.poster,
        });
    };

    const handleDelete = async (backgroundToDelete: IBackground | undefined) => {
        if (store.activePage.background === backgroundToDelete?.poster) {
            store.activePage.set({ background: theme.colors.white });
        }
        await deleteImage(backgroundToDelete?.id || '');

        setIndexDeleting(-1);
    };

    const backgroundsContent = () => {
        return backgrounds?.map((background, index) => (
            <StyledImageAssetCard
                key={index}
                item={background}
                variant={index === indexDeleting ? 'loading' : 'lite-border'}
                src={background.thumbnail || background.poster}
                onRemove={async () => {
                    setIndexDeleting(index);
                    handleDelete(background);
                }}
                onClick={async () => {
                    index !== indexDeleting && onBackgroundClick(background);
                }}
                selected={isSelected(background.poster, store)}
            />
        ));
    };

    return (
        <UploadedBackgrounds>
            <UploadButton svgSize="20px" onClick={onUploadClick} />
            {backgroundsContent()}
        </UploadedBackgrounds>
    );
});
